import React, { SyntheticEvent, useContext, useMemo, useState } from 'react';
import { getHours, getMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { WidgetProps } from '@rjsf/utils';
import styled from '@emotion/styled';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { updateTimeZone } from '@/redux/slices/authSlice';
import { selectUserDateFormat, selectUserTimeFormat, selectUserTimeZone } from '@/redux/selectors/authSelectors';
import { TIMEZONE_MAP } from '@/constants/constants';
import { DateTimeDisplay, LabelValue } from '@/exports/columnCommonComponents';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import Autocomplete from '@/components/_common/Form/Autocomplete';
import { getNestedValue } from '@/_utils/utils';
import { getYearMonthDay, makeDateInTZ } from '@/components/_common/SchemaForm/Widgets/DatePickerTimezoneWidget/utils';
import { TimeInput } from '@/components/_common/SchemaForm/Widgets/DatePickerTimezoneWidget/TimePicker';
import { Typography } from '@mui/material';

const DatePickerTimezone = (props: WidgetProps) => {
  const { label, value, required, disabled, className, onChange, readonly, rawErrors, formData } = props;

  const defaultTimeZoneConfig = useContext(ConfigContext)?.defaultTimeZone || 'UTC';
  const dispatch = useAppDispatch();
  const dateFormat = useAppSelector(selectUserDateFormat);
  const timeFormat = useAppSelector(selectUserTimeFormat);
  const userTimeZone = useAppSelector(selectUserTimeZone);
  const selectedTimeZoneLabel = userTimeZone || defaultTimeZoneConfig;
  const selectedTimeZone = TIMEZONE_MAP[selectedTimeZoneLabel];
  // The reason `valueInternal` exists is because we don't wan't datepicker to close when the date is selected,
  //  we want it to close it when the time is selected. Also without it the datepicker would disappear when user tries
  //  to type value from the keyboard.
  const [valueInternal, setValueInternal] = useState(value);
  const displayedDate = valueInternal ? utcToZonedTime(valueInternal, selectedTimeZone) : undefined;

  const handleDateChange = (date: Date | null, event?: SyntheticEvent) => {
    if (!date) {
      onChange(undefined);
      return;
    }

    /**
     * Behavior depends on how the date was changed:
     *
     * 1) Calendar UI (mouse click) or keyboard navigation:
     *    The date picker provides a Date at 00:00 for the chosen day. In this case,
     *    keep the previously selected time (hours/minutes) and only replace year, month and date.
     *
     * 2) Typing in the input field:
     *    Use the full value the user typed, including its time component.
     */
    const isClick = event?.type === 'click';
    const isKeyboard = event?.type === 'keydown';
    const preserveTime = isClick || isKeyboard;

    const hours = getHours(preserveTime ? utcToZonedTime(valueInternal, selectedTimeZone) : date);
    const minutes = getMinutes(preserveTime ? utcToZonedTime(valueInternal, selectedTimeZone) : date);

    const [year, month, day] = getYearMonthDay(date);
    setValueInternal(makeDateInTZ(selectedTimeZone, year, month, day, hours, minutes).toISOString());
  };

  const handleTimeZoneChange = (timeZone: string) => dispatch(updateTimeZone(timeZone));

  const formatOptions = useMemo(() => {
    const option = props.schema.format_options;
    if (!option) return {};
    const optionValues: any = {};

    if (option.requires) {
      option.requires.forEach((req: string) => {
        if (!optionValues.requires) optionValues.requires = [];
        optionValues.requires.push(getNestedValue(req, formData));
      });
    }
    if (option.min_value) {
      optionValues.min_value = getNestedValue(option.min_value, formData);
      if (value && new Date(value) <= new Date(optionValues.min_value)) {
        setValueInternal(undefined);
        setTimeout(() => {
          onChange(undefined);
        }, 0);
      }
    }
    if (option.max_value) {
      optionValues.max_value = getNestedValue(option.max_value, formData);
      if (value && new Date(value) >= new Date(optionValues.max_value)) {
        setValueInternal(undefined);
        setTimeout(() => {
          onChange(undefined);
        }, 0);
      }
    }
    return optionValues;
  }, [formData, props.schema.format_options]);

  const minDate = formatOptions?.min_value ? utcToZonedTime(formatOptions.min_value, selectedTimeZone) : undefined;
  const maxDate = formatOptions?.max_value ? utcToZonedTime(formatOptions.max_value, selectedTimeZone) : undefined;

  return readonly ? (
    <LabelValue label={label}>
      <DateTimeDisplay missingText="-" singleLine>
        {displayedDate}
      </DateTimeDisplay>
      <Typography variant="caption">{selectedTimeZoneLabel}</Typography>
    </LabelValue>
  ) : (
    <Container>
      <Datepicker
        label={label}
        selected={displayedDate}
        required={required || false}
        disabled={disabled || formatOptions?.requires?.some((req: string) => !req) || false}
        error={!!rawErrors?.length}
        dateFormat={`${dateFormat} ${timeFormat}`}
        className={className}
        openToDate={minDate}
        minDate={minDate}
        maxDate={maxDate}
        shouldCloseOnSelect={false}
        showTimeInput
        customTimeInput={
          <TimeInput
            selectedTime={valueInternal}
            timeInterval={15}
            timeZone={selectedTimeZone}
            min={formatOptions?.min_value}
            max={formatOptions?.max_value}
            onTimeChange={onChange}
          />
        }
        onChange={handleDateChange}
        onCalendarClose={
          // This handler is required for cases when user clicks that date (not the time), and then clicks outside and picker hides.
          //  We need to make sure we actually set the date in the form when this happens
          () => {
            if (valueInternal) {
              onChange(valueInternal);
            }
          }
        }
      />
      <Autocomplete
        fullWidth
        value={selectedTimeZoneLabel}
        options={Object.keys(TIMEZONE_MAP)}
        onChange={(_, newValue) => handleTimeZoneChange(newValue)}
        sx={{ flexBasis: '30%', '& .MuiInputBase-root': { padding: '10px!important' } }}
      />
    </Container>
  );
};

export default DatePickerTimezone;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  & div:first-of-type {
    flex: 1;
  }

  & .react-datepicker__input-time-container {
    margin: 0;
  }

  & .react-datepicker-time__caption {
    display: none !important;
  }

  & .react-datepicker__navigation--next {
    right: 85px;
  }

  & .react-datepicker-time__input-container {
    display: block !important;
  }

  & .react-datepicker-time__input {
    display: block !important;
    margin-left: 0 !important;
  }
`;
